<script setup lang="ts">
import { computed, ref } from "vue";
import moment from "moment";
import { ButtonSeverity, ButtonSize, isNullish, TuButton } from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import V2StatsQuick from "@Modules/analytics/Views/V2StatsQuick.vue";
import TimePeriods, { Period } from "@Modules/analytics/Helpers/TimePeriods";
import TimelineItemCollection from "@Modules/analytics/DTOs/TimelineItemCollection";
import { Organization } from "@Modules/teams/Data/Models/OrganizationModel";
import V2StatsTimelineTraffic from "@Modules/analytics/Views/V2StatsTimelineTraffic.vue";
import HourlyPopularityStats from "@Modules/analytics/DTOs/HourlyPopularityStats";
import DailyPopularityStats from "@Modules/analytics/DTOs/DailyPopularityStats";
import TopReferrerStats from "@Modules/analytics/DTOs/TopReferrerStats";
import PercentValueChart from "@Modules/analytics/Components/PercentValueChart/PercentValueChart.vue";
import TopLanguageStats from "@Modules/analytics/DTOs/TopLanguagesStats";
import WorldTrafficChart from "@Modules/analytics/Views/WorldTrafficChart.vue";
import WorldTrafficStats from "@Modules/analytics/DTOs/WorldTrafficStats";
import { ValidWorldMapTypes } from "@Modules/analytics/Views/WorldTrafficChart/WorldTraffic";
import TreeChart from "@Modules/analytics/Components/TreeChart/TreeChart.vue";
import GeneralStats from "@Modules/analytics/DTOs/GeneralStats";
import PieChart from "@Modules/analytics/Components/TreeChart/PieChart.vue";
import VerticalBarChart from "@Modules/analytics/Views/VerticalBarChart.vue";
import useTranslation from "@Resources/js/composables/useTranslation";
import { iAnalyticsViewModel } from "@Modules/teams/ViewModels/AnalyticsViewModel";
import ClickStat from "@Modules/analytics/DTOs/ClickStat";
import PeriodPicker from "@Modules/teams/components/PeriodPicker.vue";
import ErrorOverlay from "@Resources/js/components/teams/ErrorOverlay.vue";
import AliasToolbar from "@Modules/urls/Components/AliasToolbar.vue";
import { UrlAlias } from "@Modules/urls/Data/Models/UrlAliasFactory";
import { logError } from "@Resources/js/helpers/logger";
import StatsDisabledOverlay from "@Resources/js/components/teams/StatsDisabledOverlay.vue";
import { XAuthStore } from "@Modules/users/Store/XAuthStore";

//
// Props
interface Props {
    org: Organization,
    error?: string | null,
    currentPeriod?: Period,
    selectedAlias?: UrlAlias | null,
    selectedTag?: string | null,
    archived?: boolean,
    fetch?: iAnalyticsViewModel["fetch"],
    fetchWorldTrafficMap?: iAnalyticsViewModel["fetchWorldTrafficMap"],
    debug?: boolean,
    download?: iAnalyticsViewModel["download"],
    deleteUrl?: () => void,
    toggleArchive?: () => void,
    setStatsEnabled?: (enabled: boolean) => void,
    editUrl?: () => void,
    timeline?: TimelineItemCollection,
    timelineLoading?: boolean,
    quickstats?: ClickStat,
    quickstatsLoading?: boolean,
    populartimes?: HourlyPopularityStats,
    populartimesLoading?: boolean,
    populardays?: DailyPopularityStats,
    populardaysLoading?: boolean,
    topreferrers?: TopReferrerStats,
    topreferrersLoading?: boolean,
    toplanguages?: TopLanguageStats,
    toplanguagesLoading?: boolean,
    worldtraffic?: WorldTrafficStats,
    worldtrafficLoading?: boolean,
    browserstats?: GeneralStats,
    browserstatsLoading?: boolean,
    osstats?: GeneralStats,
    osstatsLoading?: boolean,
    devicestats?: GeneralStats,
    devicestatsLoading?: boolean,
}

interface Events {
  (e: "view-links"): void;
}

const emit = defineEmits<Events>();
const props = withDefaults(defineProps<Props>(), {
  error: null,
  currentPeriod: () => TimePeriods["last_24_hour"],
  selectedAlias: null,
  selectedTag: null,
  archived: false,
  fetch: () => Promise.resolve(),
  fetchWorldTrafficMap: () => Promise.resolve(),
  download: () => Promise.resolve(),
  deleteUrl: () => {},
  setStatsEnabled: () => {},
  toggleArchive: () => {},
  editUrl: () => {},
  timeline: () => new TimelineItemCollection([]),
  timelineLoading: false,
  quickstats: () => new ClickStat("Quickstats"),
  quickstatsLoading: false,
  populartimes: () => new HourlyPopularityStats(),
  populartimesLoading: false,
  populardays: () => new DailyPopularityStats(),
  populardaysLoading: false,
  topreferrers: () => new TopReferrerStats(),
  topreferrersLoading: false,
  toplanguages: () => new TopLanguageStats(),
  toplanguagesLoading: false,
  worldtraffic: () => new WorldTrafficStats,
  worldtrafficLoading: false,
  browserstats: () => new GeneralStats(),
  browserstatsLoading: false,
  osstats: () => new GeneralStats(),
  osstatsLoading: false,
  devicestats: () => new GeneralStats(),
  devicestatsLoading: false,
});

//
// Setup
const { __ } = useTranslation();
const authStore = XAuthStore();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);

const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Computed
const hasError = computed(() => !isNullish(props.error));

/** The main alias from the selected URL alias */
const selectedMainAlias = computed(() => props.selectedAlias?.mainAlias() ?? null);

/** Whether we have a selected alias to display */
const hasSelectedAlias = computed(() => !isNullish(selectedMainAlias.value));
const statsEnabled = computed(() => selectedMainAlias.value?.stats.enabled);

/** Whether we have a selected tag to display */
const hasSelectedTag = computed(() => !isNullish(props.selectedTag));

/** The earliest date analytics data is available, capped at 1 year */
const maximumAvailableTime = computed<moment.Moment | null>(() => {
  const userCreatedAt = authStore.user?.created_at;
  let createdAt: moment.Moment | null = null;

  if (!isNullish(selectedMainAlias.value)) {
    createdAt = selectedMainAlias.value.createdAt();
  } else if (!isNullish(userCreatedAt)) {
    createdAt = moment(userCreatedAt);
  }

  if (isNullish(createdAt)) {
    return null;
  }

  const oneYearAgo = moment().subtract(1, "year");

  return createdAt.isBefore(oneYearAgo) ? oneYearAgo : createdAt;
});

/** The kicker text above the title */
const kickerText = computed(() => {
  if (hasSelectedAlias.value) {
    return __("organization_dashboard.kicker.one_link_analytics");
  }
  if (hasSelectedTag.value) {
    return __("organization_dashboard.kicker.tag_analytics");
  }
  return __("organization_dashboard.kicker.all_links_analytics");
});

//
// Data
const availableStatQuicks = computed<(keyof ClickStat)[]>(() => {
    if(!!props.selectedAlias) {
        return ["human", "unique"];
    }

    return ["active", "human", "unique", "visitors"];
});
const isDownloading = ref(false);

//
// Methods

function refreshStats(): void {
  props.fetch(props.currentPeriod, props.archived);
}

async function onDownloadStats(): Promise<void> {
  if (isDownloading.value) {
    return;
  }

  isDownloading.value = true;
  await props.download?.({ period: props.currentPeriod, archived: props.archived });
  isDownloading.value = false;
}

function onPeriodSelected(period: Period): void {
  props.fetch(period, props.archived);
}

function onWorldMapChange(map: ValidWorldMapTypes): void {
  props.fetchWorldTrafficMap(map);
}

function toggleStats(): void {
  if (isNullish(statsEnabled.value)) {
    logError("[AnalyticsDashboard][toggleStats]: Main alias stats is undefined.")

    return;
  }

  props.setStatsEnabled(!statsEnabled.value)
}

// Used to expose methods and vars for testing
defineExpose({
  refreshStats,
});
</script>

<template>
  <div
    class="analytics-dashboard-wrapper _analytics_panel"
    :class="{ 'analytics-dashboard-wrapper--placeholder-height': isMobileView && hasError}"
  >
    <ErrorOverlay
      v-if="hasError"
      class="_error_overlay"
      @retry="refreshStats"
    />
    <div
      v-else
      class="analytics-dashboard"
      :class="{ 'analytics-dashboard--mobile': isMobileView }"
    >
      <TuButton
        v-if="isMobileView"
        class="_view_links"
        label="View Links"
        @click="emit('view-links')"
      />

      <div class="analytics-dashboard__header">
        <p class="analytics-dashboard__kicker _kicker">
          {{ kickerText }}
        </p>
        <h3 class="analytics-dashboard__title _title">
          {{ org.name.toUpperCase() }}
        </h3>
      </div>

      <AliasToolbar
        v-if="hasSelectedAlias && selectedAlias"
        :url-alias="selectedAlias"
        @edit="editUrl"
        @delete="deleteUrl"
        @toggle-archive="toggleArchive"
        @toggle-stats="toggleStats"
      />

      <div
        v-if="hasSelectedTag && selectedTag"
        class="analytics-dashboard__tag-header _tag_header"
      >
        <TuButton
          :label="selectedTag"
          :severity="ButtonSeverity.CANCEL"
          :size="ButtonSize.MEDIUM"
          icon-class="fa-solid fa-tag"
        />
      </div>

      <StatsDisabledOverlay
        v-if="!statsEnabled && hasSelectedAlias"
        @enable-stats="toggleStats"
      />
      <template v-else>
        <div class="analytics-dashboard__action-hub">
          <TuButton
            class="_refresh analytics-dashboard__refresh-button"
            label="Refresh"
            icon-class="fa-solid fa-arrows-rotate"
            is-outlined
            :severity="ButtonSeverity.CANCEL"
            @click="refreshStats"
          />
          <PeriodPicker
            class="_period-picker"
            :selected-period="currentPeriod"
            :maximum-available-time="maximumAvailableTime"
            @period-selected="onPeriodSelected"
          />
          <TuButton
            class="_download"
            label=""
            icon-class="fa-solid fa-download"
            is-outlined
            :disabled="isDownloading"
            :size="ButtonSize.LARGE"
            :severity="ButtonSeverity.CANCEL"
            @click="onDownloadStats"
          />
        </div>

        <V2StatsQuick
          :available-stats="availableStatQuicks"
          :dataset="quickstats"
          :loading="quickstatsLoading"
          :simple="!!selectedAlias"
        />

        <V2StatsTimelineTraffic
          class="_timeline"
          :dataset="timeline"
          :loading="timelineLoading"
        />

        <WorldTrafficChart
          class="_world_traffic"
          :dataset="worldtraffic"
          :loading="worldtrafficLoading"
          :debug="debug"
          @map-change="onWorldMapChange"
        />

        <div class="analytics-dashboard__popular-charts">
          <VerticalBarChart
            class="_popular_hour"
            :title="__('chart.title.popularity_hour')"
            :dataset="populartimes"
            :loading="populartimesLoading"
          />
          <VerticalBarChart
            class="_popular_weekday"
            :title="__('chart.title.popularity_weekday')"
            :dataset="populardays"
            :loading="populardaysLoading"
          />
        </div>

        <div class="analytics-dashboard__top-charts">
          <PercentValueChart
            class="analytics-dashboard__top-chart _top_referrers"
            :title="__('chart.title.top_referrer')"
            :dataset="topreferrers"
            :loading="topreferrersLoading"
          />
          <PercentValueChart
            class="analytics-dashboard__top-chart _top_languages"
            :title="__('chart.title.top_languages')"
            :dataset="toplanguages"
            :loading="toplanguagesLoading"
          />
        </div>

        <div class="analytics-dashboard__bottom-charts">
          <TreeChart
            class="_browsers"
            title="Top Browsers"
            :dataset="browserstats"
            :loading="browserstatsLoading"
          />
          <TreeChart
            class="_os"
            title="Top OS"
            :dataset="osstats"
            :loading="osstatsLoading"
          />
          <PieChart
            class="_devices"
            title="Top Devices"
            :dataset="devicestats"
            :loading="devicestatsLoading"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.analytics-dashboard-wrapper {
  height: 100%;
  padding: spacing.$xxs spacing.$xs;
  overflow-y: auto;

  background-color: color.$background-primary;

  &--placeholder-height {
    display: flex;
    flex-flow: column;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
  }
}

.analytics-dashboard {
  $self: &;

  display: flex;
  flex-flow: column;
  gap: spacing.$xxs;

  &__kicker {
    color: color.$text-secondary;
    font-size: typography.$base-font-size;
    line-height: typography.$base-line-height;
    font-weight: typography.$font-weight-semi-bold;
  }

  &__title {
    font-size: typography.$h3-font-size;
    line-height: typography.$h3-line-height;
    font-weight: typography.$font-weight-bold;
  }

  &__action-hub {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: spacing.$xxxs;
  }

  &__refresh-button {
    margin-right: auto;
  }

  &__bottom-charts {
    display: grid;
    gap: spacing.$xxs;
    grid-template-columns: 1fr 1fr 1fr;
    min-height: 500px;
  }

  &__popular-charts,
  &__top-charts {
    display: grid;
    gap: spacing.$xxs;
    grid-template-columns: 1fr 1fr;
  }

  &__kicker,
  &__title {
    margin: 0;
  }

  &__tag-header {
    display: flex;
    align-items: center;
    gap: spacing.$xxxs;
    padding: spacing.$xxxs 0;
  }

  &--mobile {
    #{ $self }__title {
      word-break: break-all;
    }

    #{ $self }__popular-charts,
    #{ $self }__top-charts {
      grid-template-columns: 1fr;
    }

    #{ $self }__bottom-charts {
      grid-template-columns: 1fr;
    }
  }
}
</style>
